import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox } from '@xingternal/checkbox';
import { IconArrowRight } from '@xingternal/icons';
import { xdlColorDisabled, xdlColorText } from '@xingternal/tokens';

import { FullScreenModal } from '@/components/full-screen-modal';
import { TaskHeader } from '@/components/task-header';
import { Aggregations, FilterResult } from '@/domain/search/helpers';
import { JobSearchFilterCollectionSplitBenefitsFragment, Maybe } from '@/graphql/generated';
import { buildSearchCriteria } from '@/helpers/build-search-criteria';

import { EntityIdJobSearchFilter } from '../types';

import * as SF from '../filters.styles';
import * as ST from './two-level-filter.styles';

type ParseFiltersToSearchCriteriaArgs = {
  filter: FilterResult;
  selectedFilters: Maybe<JobSearchFilterCollectionSplitBenefitsFragment>[];
};

const filterOptionSelectedFilters = ({
  filter: filterResult,
  selectedFilters,
}: ParseFiltersToSearchCriteriaArgs): Maybe<EntityIdJobSearchFilter>[] => {
  const name = filterResult.propertyName;
  const typename = `JobFilter${name.charAt(0).toUpperCase()}${name.slice(1)}`;

  return selectedFilters.filter(
    (filter) => filter && filter.__typename === typename && 'entityId' in filter
  ) as EntityIdJobSearchFilter[];
};

type TwoLevelFilterProps = {
  aggregations?: Aggregations[keyof Aggregations];
  filter: FilterResult;
  isLoading: boolean;
  selectedFilters: Maybe<JobSearchFilterCollectionSplitBenefitsFragment>[];
  onFilterClick: (id: string) => void;
};

export const TwoLevelFilter: React.VFC<TwoLevelFilterProps> = ({
  aggregations,
  filter,
  isLoading,
  selectedFilters,
  onFilterClick,
}) => {
  const intl = useIntl();
  const [isSecondLevelShown, setIsSecondLevelShown] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const optionSelectedFilters = useMemo(
    () => filterOptionSelectedFilters({ filter, selectedFilters }),
    [filter, selectedFilters]
  );
  const filterDescription = buildSearchCriteria({ filterCollection: optionSelectedFilters }, intl);

  const handleOnOpenNestedModal = (): void => {
    setIsSecondLevelShown(true);
  };

  const handleOnCloseNestedModal = (): void => {
    setIsSecondLevelShown(false);
  };

  const handleOnFilterClick = (id: string) => (): void => {
    onFilterClick(id);
  };

  useEffect(() => {
    // This allows us to ensure that when the aggregations are loading on filter may change
    // from disabled to enabled, and after finishing loading, back to disabled
    // Same for the aggregations, if its loading, they dissapear
    if (!isLoading) {
      setIsDisabled(() => (!aggregations || aggregations.length === 0) && optionSelectedFilters.length === 0);
    }
  }, [aggregations, isLoading, optionSelectedFilters]);

  return (
    <>
      <SF.ClickableFiltersContainer disabled={isDisabled} onClick={!isDisabled ? handleOnOpenNestedModal : undefined}>
        <SF.FiltersTitleContainer>
          <SF.FilterHeadline $disabled={isDisabled} noMargin>
            <FormattedMessage id={filter.titleKey} />
          </SF.FilterHeadline>
          <IconArrowRight color={isDisabled ? xdlColorDisabled : xdlColorText} width={24} />
        </SF.FiltersTitleContainer>
        {filterDescription ? (
          <SF.FilterDescription size="xsmall" noMargin>
            {filterDescription}
          </SF.FilterDescription>
        ) : null}
      </SF.ClickableFiltersContainer>
      <SF.FilterDivider $noMargin />
      <FullScreenModal show={isSecondLevelShown} nested>
        <TaskHeader
          title={<FormattedMessage id={filter.titleKey} />}
          backDisabled={(isDisabled || isLoading) && !(aggregations?.length === 0)}
          onBack={handleOnCloseNestedModal}
        />
        <ST.FilterListContainer>
          {aggregations && aggregations.length > 0
            ? aggregations.map((aggregation) => {
                const id = aggregation.id;
                const selected = optionSelectedFilters.findIndex((filter) => filter?.entityId === id) > -1;
                return aggregation[filter.paramName] ? (
                  <ST.FilterListItemText
                    key={id}
                    data-cy="two-level-filter-item"
                    $selected={selected}
                    $disabled={isDisabled || isLoading}
                  >
                    {aggregation[filter.paramName].localizationValue} ({aggregation.count})
                    <Checkbox on={selected} onChange={handleOnFilterClick(id)} disabled={isDisabled || isLoading} />
                  </ST.FilterListItemText>
                ) : null;
              })
            : optionSelectedFilters.map((selected) =>
                selected ? (
                  <ST.FilterListItemText
                    key={selected.entityId}
                    data-cy="two-level-filter-item"
                    $selected
                    $disabled={isLoading}
                  >
                    {selected[filter.propertyName].localizationValue}
                    <Checkbox onChange={handleOnFilterClick(selected.entityId)} disabled={isLoading} on />
                  </ST.FilterListItemText>
                ) : null
              )}
        </ST.FilterListContainer>
      </FullScreenModal>
    </>
  );
};
